import { combineEpics, ofType } from 'redux-observable';
import { EMPTY } from 'rxjs';
import { concatMap, mergeMap } from 'rxjs/operators';
import {
  getNumberRangePrivateMethod,
  searchAddressPublicMethod,
  searchPagesPublicMethod,
} from '../../generated/api/uiApiMethods.js';
import {
  TypeKeys,
  loadNumberRangeFailed,
  loadNumberRangeFulfilled,
  searchAddressFailed,
  searchAddressFulfilled,
  searchPublicPagesFailed,
  searchPublicPagesFulfilled,
} from '../actions/index.js';
import { redirectWithState } from '../common/redirect.js';
import { callUiApi, prepareUiApiRequest } from '../common/uiApiUtils.js';
import { actionToActionState, getMdmIdHeaderFromUrlParams } from './epicUtils.js';
import type { ActionAndState, EpicDependencies } from './epicUtils.js';
import type { AddressSearchResponse, NumberRangeResponse, PageSearchResponse } from '../../generated/api/models.js';
import type {
  ActionTypes,
  HardRedirectAction,
  LoadNumberRangeAction,
  SearchAddressAction,
  SearchPublicPagesAction,
} from '../actions/index.js';
import type { State } from '../common/store.js';
import type { Action } from 'redux';
import type { ActionsObservable, Epic, StateObservable } from 'redux-observable';

const loadNumberRange: Epic<ActionTypes, Action, State, EpicDependencies> = (
  action$: ActionsObservable<ActionTypes>,
  state$: StateObservable<State>,
  epicDependencies: EpicDependencies
) =>
  prepareUiApiRequest(action$.pipe(ofType(TypeKeys.LOAD_NUMBER_RANGE)), action =>
    actionToActionState(action, state$, 'resources')
  ).pipe(
    concatMap((actionAndState: ActionAndState) => {
      const action = actionAndState.action as LoadNumberRangeAction;
      const successAction = (response: NumberRangeResponse) =>
        loadNumberRangeFulfilled(action.rangeId, response.numberCategory, response.numbers);
      return callUiApi({
        epicDependencies,
        failureAction: (message, status, errors, params) =>
          loadNumberRangeFailed(action.rangeId, message, status, errors, params),
        method: getNumberRangePrivateMethod(action.rangeId),
        state$,
        successAction,
        headers: getMdmIdHeaderFromUrlParams(),
      });
    })
  );

const searchAddressEpic: Epic<ActionTypes, Action, State, EpicDependencies> = (
  action$: ActionsObservable<ActionTypes>,
  state$: StateObservable<State>,
  epicDependencies: EpicDependencies
) =>
  prepareUiApiRequest(action$.pipe(ofType(TypeKeys.SEARCH_ADDRESS)), action =>
    actionToActionState(action, state$, 'resources')
  ).pipe(
    concatMap((actionAndState: ActionAndState) => {
      const action = actionAndState.action as SearchAddressAction;
      return callUiApi({
        epicDependencies,
        state$,
        method: searchAddressPublicMethod({ query: action.query, postalCode: action.postalCode }),
        headers: getMdmIdHeaderFromUrlParams(),
        successAction: (response: AddressSearchResponse) =>
          searchAddressFulfilled({ ...response, postalCode: action.postalCode }),
        failureAction: searchAddressFailed,
      });
    })
  );

const searchPublicPages: Epic<ActionTypes, Action, State, EpicDependencies> = (
  action$: ActionsObservable<ActionTypes>,
  state$: StateObservable<State>,
  epicDependencies: EpicDependencies
) =>
  prepareUiApiRequest(action$.pipe(ofType(TypeKeys.SEARCH_PUBLIC_PAGES)), action =>
    actionToActionState(action, state$, 'resources')
  ).pipe(
    concatMap((actionAndState: ActionAndState) => {
      const action = actionAndState.action as SearchPublicPagesAction;
      return callUiApi({
        epicDependencies,
        state$,
        method: searchPagesPublicMethod(action),
        successAction: (response: PageSearchResponse) =>
          searchPublicPagesFulfilled(action.query, action.start, response),
        failureAction: searchPublicPagesFailed,
      });
    })
  );

/**
 * Hard redirect is made so that sessionStorage data can be moved to another page
 * where content-security-policy (csp) has now changed and allows analytics to work properly
 */
const getHardRedirect: Epic<ActionTypes, Action, State, EpicDependencies> = (
  action$: ActionsObservable<ActionTypes>,
  state$: StateObservable<State>
) => {
  return action$.pipe(
    ofType(TypeKeys.HARD_REDIRECT),
    mergeMap((action: HardRedirectAction) => {
      redirectWithState(state$.value, action.path);
      return EMPTY;
    })
  );
};

export const resourcesEpic: Epic<ActionTypes, Action, State, EpicDependencies> = combineEpics(
  loadNumberRange,
  searchAddressEpic,
  searchPublicPages,
  getHardRedirect
);
